import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as XLSX from 'xlsx';

export interface ExcelUploadResult {
  filename: string;
  data: any[];
  headers: string[];
  rowCount: number;
}

export interface UploadProgress {
  percentage: number;
  stage: 'reading' | 'processing' | 'validating' | 'complete';
  message: string;
}

@Component({
  selector: 'app-excel-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './excel-upload.component.html',
  styleUrl: './excel-upload.component.css'
})
export class ExcelUploadComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput', { static: true }) fileInput!: ElementRef<HTMLInputElement>;
  
  @Input() acceptedTypes: string = '.xlsx,.xls';
  @Input() maxFileSize: number = 10 * 1024 * 1024; // 10MB default
  @Input() allowMultiple: boolean = false;
  @Input() disabled: boolean = false;
  
  @Output() fileSelected = new EventEmitter<File>();
  @Output() uploadProgress = new EventEmitter<UploadProgress>();
  @Output() uploadComplete = new EventEmitter<ExcelUploadResult>();
  @Output() uploadError = new EventEmitter<string>();

  private destroy$ = new Subject<void>();
  
  isDragOver = false;
  isUploading = false;
  progress: UploadProgress = {
    percentage: 0,
    stage: 'reading',
    message: ''
  };
  
  selectedFile: File | null = null;
  uploadResult: ExcelUploadResult | null = null;
  error: string | null = null;

  constructor() {}

  ngOnInit(): void {
    // Component initialization
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.disabled && !this.isUploading) {
      this.isDragOver = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.disabled || this.isUploading) {
      return;
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileSelection(input.files[0]);
    }
  }

  openFileDialog(): void {
    if (!this.disabled && !this.isUploading) {
      this.fileInput.nativeElement.click();
    }
  }

  private handleFileSelection(file: File): void {
    this.error = null;
    this.uploadResult = null;
    
    // Validate file
    const validation = this.validateFile(file);
    if (!validation.isValid) {
      this.error = validation.message;
      this.uploadError.emit(validation.message);
      return;
    }

    this.selectedFile = file;
    this.fileSelected.emit(file);
    this.processExcelFile(file);
  }

  private validateFile(file: File): { isValid: boolean; message: string } {
    // Check file type
    const validTypes = this.acceptedTypes.split(',').map(type => type.trim());
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!validTypes.includes(fileExtension)) {
      return {
        isValid: false,
        message: `File type ${fileExtension} is not supported. Accepted types: ${this.acceptedTypes}`
      };
    }

    // Check file size
    if (file.size > this.maxFileSize) {
      const maxSizeMB = (this.maxFileSize / (1024 * 1024)).toFixed(1);
      return {
        isValid: false,
        message: `File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds maximum allowed size of ${maxSizeMB}MB`
      };
    }

    return { isValid: true, message: '' };
  }

  private async processExcelFile(file: File): Promise<void> {
    this.isUploading = true;
    
    try {
      // Stage 1: Reading file
      this.updateProgress(10, 'reading', 'Reading Excel file...');
      
      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      
      // Stage 2: Processing
      this.updateProgress(40, 'processing', 'Processing Excel data...');
      
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // Stage 3: Validating
      this.updateProgress(70, 'validating', 'Validating data structure...');
      
      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
      
      if (jsonData.length === 0) {
        throw new Error('Excel file is empty or contains no data');
      }

      // Extract headers and data
      const headers = jsonData[0] as string[];
      const data = jsonData.slice(1);
      
      // Stage 4: Complete
      this.updateProgress(100, 'complete', 'Upload complete!');
      
      const result: ExcelUploadResult = {
        filename: file.name,
        data: data,
        headers: headers,
        rowCount: data.length
      };

      this.uploadResult = result;
      this.uploadComplete.emit(result);
      
      // Reset progress after a short delay
      setTimeout(() => {
        this.resetUpload();
      }, 2000);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process Excel file';
      this.error = errorMessage;
      this.uploadError.emit(errorMessage);
      this.isUploading = false;
    }
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result instanceof ArrayBuffer) {
          resolve(e.target.result);
        } else {
          reject(new Error('Failed to read file as ArrayBuffer'));
        }
      };
      reader.onerror = () => reject(new Error('File reading failed'));
      reader.readAsArrayBuffer(file);
    });
  }

  private updateProgress(percentage: number, stage: UploadProgress['stage'], message: string): void {
    this.progress = { percentage, stage, message };
    this.uploadProgress.emit(this.progress);
  }

  resetUpload(): void {
    this.selectedFile = null;
    this.uploadResult = null;
    this.error = null;
    this.isUploading = false;
    this.progress = { percentage: 0, stage: 'reading', message: '' };
    
    // Clear file input
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  // Helper methods for template usage
  getProgressBarClass(): string {
    switch (this.progress.stage) {
      case 'reading': return 'bg-info';
      case 'processing': return 'bg-warning';
      case 'validating': return 'bg-primary';
      case 'complete': return 'bg-success';
      default: return 'bg-secondary';
    }
  }

  getFileSize(): string {
    if (!this.selectedFile) return '';
    const size = this.selectedFile.size;
    if (size < 1024) return `${size} bytes`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatNumber(num: number): string {
    return new Intl.NumberFormat().format(num);
  }
}